import React, { useEffect, useRef, useState } from "react";
import { useAppServices } from "hook/services";
import { useAgencyInfo } from "context/agency";
import { Delete } from "@mui/icons-material";
import SoftButton from "components/SoftButton";
import toast from "react-hot-toast";
import { uploadImageToGhl } from "hook/services";
import { FaGoogle } from "react-icons/fa";
import PopUp from "./Component/PopUp";
import ReactCropModal from "layouts/super-admin/settings/Configurator/components/ReactCropModal/ReactCropModal";

function Attachments({ formData, handleRefresh }) {
  const AppService = useAppServices();
  const [agency] = useAgencyInfo();
  const [loadingButton, setLoadingButton] = useState(false);
  const [attachments, setAttachments] = useState([]);

  // Crop modal states
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [tempImageFile, setTempImageFile] = useState(null);

  useEffect(() => {
    if (formData?.attachments?.length) {
      setAttachments(formData.attachments);
    }
  }, [formData]);

  // Open crop modal when file chosen
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setTempImageFile(file);
    setCurrentImage(URL.createObjectURL(file));
    setCropModalOpen(true);
    e.target.value = ""; // reset input so same file can be re-selected
  };

  // After crop, upload image
  const handleCroppedImage = async (blob) => {
    if (!blob) return;

    setLoadingButton(true);
    try {
      const file = new File([blob], "cropped-attachment.jpg", {
        type: "image/jpeg",
      });
      const image_response = await uploadImageToGhl(file, agency);

      if (image_response.success) {
        const newAttachments = [...attachments, image_response.data];
        const payload = { _id: formData.id, attachments: newAttachments };

        const { response } = await AppService.accounts.update({ payload });
        if (response) {
          setAttachments(newAttachments);
          toast.success("Image Uploaded");
        } else {
          toast.error("Error updating image");
        }
      } else {
        toast.error("Failed to upload image");
      }
    } catch (err) {
      console.error("Image upload failed:", err);
      toast.error("Error uploading image");
    } finally {
      setLoadingButton(false);
      setCropModalOpen(false);
      setTempImageFile(null);
    }
  };

  const handleDelete = async (index) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    setAttachments(newAttachments);

    const payload = { _id: formData.id, attachments: newAttachments };
    const { response } = await AppService.accounts.update({ payload });

    if (response) {
      handleRefresh();
      toast.success("Delete Successfully");
    } else {
      toast.error("Error deleting attachment");
    }
  };

  // Google images
  const [googleModelOpen, setGoogleModelOpen] = useState(false);
  const [googleImages, setGoogleImages] = useState([]);
  const [selectedGoogleImages, setSelectedGoogleImages] = useState([]);
  const [loading, SetLoading] = useState(false);

  const GetGoogleImages = async () => {
    const { response } = await AppService.reviews.GetGoogleImages({
      query: `place_id=${formData?.google_place_id}&Apikey=${agency?.google_image_api_key}`,
    });
    if (response) {
      setGoogleImages(response?.data || []);
    }
  };

  useEffect(() => {
    if (formData?.google_place_id) GetGoogleImages();
  }, [formData?.google_place_id]);

  const toggleImage = (imageUrl) => {
    setSelectedGoogleImages((prev) =>
      prev.includes(imageUrl)
        ? prev.filter((url) => url !== imageUrl)
        : [...prev, imageUrl]
    );
  };

  const handleUploadSelectedImages = async () => {
    SetLoading(true);
    try {
      const files = await Promise.all(
        selectedGoogleImages.map(async (url, idx) => {
          const blob = await fetch(url).then((r) => r.blob());
          const ext = blob.type.split("/")[1] || "jpg";
          return new File([blob], `google-${Date.now()}-${idx}.${ext}`, {
            type: blob.type,
          });
        })
      );

      const uploadResults = await Promise.all(
        files.map((file) => uploadImageToGhl(file, agency))
      );

      const uploadedData = uploadResults
        .filter((r) => r?.success)
        .map((r) => r.data);

      if (!uploadedData.length) {
        toast.error("No image was uploaded");
        return;
      }

      const newAttachments = [...attachments, ...uploadedData];
      const payload = { _id: formData.id, attachments: newAttachments };
      const { response } = await AppService.accounts.update({ payload });

      if (response) {
        setAttachments(newAttachments);
        toast.success(
          `${uploadedData.length} image${uploadedData.length > 1 ? "s" : ""
          } uploaded`
        );
      } else {
        toast.error("Error updating image list");
      }
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    } finally {
      setSelectedGoogleImages([]);
      setGoogleModelOpen(false);
      SetLoading(false);
    }
  };

  // Button hover animations
  const buttonRef = useRef(null);
  const textRef = useRef(null);

  const handleMouseEnter = () => {
    if (buttonRef.current && textRef.current) {
      buttonRef.current.style.width = "200px";
      buttonRef.current.style.paddingRight = "20px";
      textRef.current.style.opacity = "1";
    }
  };

  const handleMouseLeave = () => {
    if (buttonRef.current && textRef.current) {
      buttonRef.current.style.width = "50px";
      buttonRef.current.style.paddingRight = "10px";
      textRef.current.style.opacity = "0";
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "end",
          marginBottom: "20px",
        }}
      >
        <button
          type="button"
          ref={buttonRef}
          onClick={() => setGoogleModelOpen(true)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            border: "none",
            backgroundColor: "black",
            color: "white",
            padding: "10px",
            borderRadius: "50px",
            cursor: "pointer",
            overflow: "hidden",
            transition: "width 0.3s ease, padding 0.3s ease",
            width: "50px",
            whiteSpace: "nowrap",
          }}
        >
          <FaGoogle size={22} style={{ flexShrink: 0 }} />
          <span
            ref={textRef}
            style={{
              opacity: 0,
              color: "white",
              transition: "opacity 0.3s ease",
            }}
          >
            Upload from Google
          </span>
        </button>

        <SoftButton
          color={"black"}
          variant={"contained"}
          className="buttonupload"
          style={{ marginLeft: "10px", cursor: "pointer" }}
        >
          <label>
            <div>
              <svg
                width="30"
                height="30"
                viewBox="0 0 14 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ cursor: "pointer" }}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.38444 3.98444L4.99555 5.68667L4.58611 5.31056L6.66222 2.77778L8.74722 5.325L8.32889 5.69444L6.94 3.98556V8.33333H6.38444V3.98444ZM10.2778 9.44444H3.05556C1.67722 9.44444 0.555556 8.32333 0.555556 6.94444C0.555556 5.73444 1.41944 4.70111 2.61 4.48667L3.04278 4.40889L3.06667 3.97056C3.16944 2.055 4.75056 0.555556 6.66667 0.555556C8.58278 0.555556 10.1639 2.055 10.2656 3.97056L10.29 4.40889L10.7233 4.48667C11.9133 4.70111 12.7778 5.73444 12.7778 6.94444C12.7778 8.32333 11.6561 9.44444 10.2778 9.44444ZM10.8211 3.94C10.7033 1.745 8.89222 0 6.66667 0C4.44111 0 2.62944 1.745 2.51167 3.94C1.08389 4.19722 0 5.44333 0 6.94444C0 8.63167 1.36833 10 3.05556 10H10.2778C11.965 10 13.3333 8.63167 13.3333 6.94444C13.3333 5.44333 12.2494 4.19722 10.8211 3.94Z"
                  fill="#ffffff"
                />
              </svg>
            </div>
            <input
              disabled={loadingButton}
              hidden
              accept="image/*"
              type="file"
              onChange={handleImageChange}
            />
          </label>
        </SoftButton>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
        {attachments?.map((attachment, index) => (
          <div key={index} style={{ position: "relative" }}>
            <div
              onClick={() => handleDelete(index)}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                cursor: "pointer",
                color: "red",
                background: "white",
                padding: "5px",
                borderRadius: "4px",
              }}
            >
              <Delete />
            </div>
            <img src={attachment} alt="attachment" width={300} />
          </div>
        ))}
      </div>

      {/* Google Image Modal */}
      <PopUp
        open={googleModelOpen}
        handleClose={() => setGoogleModelOpen(false)}
        title={`${loading ? "Image Uploading...." : "Select Image From Google"}`}
      >
        {googleImages?.length > 0 ? (
          <div
            style={{
              display: "flex",
              gap: "20px",
              padding: "10px",
              flexWrap: "wrap",
              overflowX: "auto",
            }}
          >
            {googleImages.map((imageUrl, index) => (
              <div
                key={index}
                style={{
                  minWidth: "120px",
                  backgroundColor: selectedGoogleImages.includes(imageUrl)
                    ? "#d0f0d0"
                    : "#f9f9f9",
                  padding: "10px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  position: "relative",
                }}
              >
                <img
                  src={imageUrl}
                  alt={`Google place image ${index + 1}`}
                  style={{
                    width: "120px",
                    height: "120px",
                    borderRadius: "8px",
                    objectFit: "cover",
                  }}
                />
                <input
                  style={{
                    position: "absolute",
                    top: "10px",
                    right: "20px",
                    zIndex: 1000,
                    width: "20px",
                    height: "20px",
                    cursor: "pointer",
                  }}
                  type="checkbox"
                  onChange={() => toggleImage(imageUrl)}
                />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No images available.</p>
        )}
        {selectedGoogleImages.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <button
              onClick={handleUploadSelectedImages}
              style={{
                backgroundColor: "black",
                color: "white",
                padding: "10px 20px",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              {loading
                ? "Uploading..."
                : `Upload Selected (${selectedGoogleImages.length})`}
            </button>
          </div>
        )}
      </PopUp>
      <ReactCropModal
        open={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        currentImage={currentImage}
        onCropComplete={handleCroppedImage}
      />
    </div>
  );
}

export default Attachments;
